import { ref } from 'vue';
import { useUpc } from '@common-vue/media-card-large';
import { useDeviceType } from '@composables/utils/useDeviceType';
import useMarxent from '@composables/useMarxent';
import useMarxentButton from '@composables/useMarxentButton';
import { useModal } from '@composables/utils/useModal';
import { usePageData } from '@composables/usePageData';
import { useEnvProps } from '@composables/useEnvProps';
import { fireLinkTag } from '../../features/analytics/tagManager';

/**
 * @typedef {Object} Returns
 * @property {boolean} isArEligible
 * @property {boolean} isAdded
 * @property {function} setCtaConfig
 * @property {function} onMarxentButtonClick
 * @property {object} marxentCtaConfig
 */

/**
   * Get the QR URL for a specific SKU and API credentials.
   * @returns {{
 *  isArEligible: import('vue').ComputedRef<boolean>,
 *  isAdded: import('vue').Ref<boolean>,
 *  marxentCtaConfig: import('vue').Ref<Object>,
 *  setCtaConfig: function,
 *  onMarxentButtonClick: function,
 * }}.
*/

export default function useMarxentCta() {
  const { isDesktop } = useDeviceType();
  const { productId } = usePageData();
  const { selectedUpcNumber } = useUpc(productId);
  const { open } = useModal();
  const { marxentApiCredentials } = useEnvProps();
  const { initMarxent } = useMarxent();
  const {
    marxentValues,
    checkForMarxentAssetsAndUrl,
    isArEligible,
    isArReady,
    marxentButtonStyles,
    autoStartMarxentExperience,
  } = useMarxentButton(initMarxent, marxentApiCredentials);

  const marxentCtaConfig = ref({});
  const isAdded = ref(false);

  const onMarxentButtonClick = async () => {
    // Desktop case
    if (isDesktop.value && isArEligible.value) {
      open('marxent'); // opens marxent qr modal
      // For Mobile we're using an a tag with href
      const data = {
        event_name: 'marxent qr code open',
        custom_link_name: 'marxent qr code open',
      };
      fireLinkTag(data);
    }
  };

  const setCtaConfig = async () => {
    await checkForMarxentAssetsAndUrl(selectedUpcNumber.value);
    if (isArReady.value && isArEligible.value) {
      marxentCtaConfig.value = {
        customCssClass: 'marxent font-small',
        customStyle: marxentButtonStyles,
        id: 'marxent',
        label: 'VIEW IN YOUR ROOM',
        isAR: !isDesktop.value,
        onClickHandler: onMarxentButtonClick,
        url: isDesktop.value ? null : marxentValues.value?.marxentUrl,
      };
    }
  };

  if (isArEligible.value && !isDesktop.value) {
    autoStartMarxentExperience();
  }

  return {
    setCtaConfig,
    isArEligible,
    marxentCtaConfig,
    isAdded,
    onMarxentButtonClick,
  };
}
